import React from 'react';
import { Animated, View, KeyboardAvoidingView } from 'react-native';
import { connect } from 'react-redux';

import { KEYBOARD_AVOID_BEHAVIOR, isPhone, isWeb, isAndroid, navigateBack } from '@helpers';
import { scale, height, width, fullHeight, fullWidth, notchTop, notchBottom } from '@components/frame';

import ColorsWrapper from '@styles/colors';
import { MODAL_ANIMATION_TIMING, modalBg, SCREEN_BORDER_RADIUS } from '@v2/pages/common/constants/base.constants';
import ModalWrapper from '@components/modal/ModalWrapper';

const FrameComponent = (props) => {
  const {
    C,
    Component,
    width: modalWidth,
    bg = modalBg,
    backdropColor = 'Overlay/Light/Dim strong',
    centered,
    asAlert,
    paddingHorizontal,
    paddingLeft,
    hide,
    id,
    avoidingViewEnable = true,
    noPhoneHeight,
    isPhoneSmall,
    offsetBottom = scale(isPhone ? 0 : 50),
    offsetTop = scale(isPhone ? 0 : 50),
    useFullHeight,
    bottomSheet,
    borderRadius = 9,
    centeredHorizontally = true,
    centeredVertically = true,
    shouldCloseOnPressOutside = false,
    callBackOnClose,
    isVisible = true,
    animationIn = bottomSheet ? 'slideInUp' : 'fadeIn',
    animationOut = bottomSheet ? 'slideOutDown' : 'fadeOut',
  } = props;

  const maxHeight = (useFullHeight ? fullHeight : height) - (offsetBottom + offsetTop + notchBottom);

  const newProps = { ...props, maxHeight };
  delete newProps.C;
  delete newProps.ColorsData;
  delete newProps.isVisible;
  delete newProps.animationIn;
  delete newProps.animationOut;

  const handleClose = () => {
    hide();
    callBackOnClose?.();
  };

  const Content = (
    <View
      style={{
        flex: 1,
        ...(centeredHorizontally ? { alignItems: 'center' } : {}),
      }}
    >
      <KeyboardAvoidingView
        style={[
          {
            flex: 1,
            minHeight: '100%',
            flexDirection: 'column',
          },
        ]}
        behavior={KEYBOARD_AVOID_BEHAVIOR}
        enabled={avoidingViewEnable}
      >
        <Animated.View
          style={[
            {
              top: isPhoneSmall && isPhone ? '45%' : 0,
              height: isPhoneSmall && isPhone ? '55%' : '100%',
            },
          ]}
        >
          <View
            style={{
              height: '100%',
              ...(centeredVertically ? { justifyContent: bottomSheet ? 'flex-end' : 'center' } : {}),
              paddingTop: isPhone ? scale(20) + (!isAndroid ? notchTop : 0) : offsetTop,
              paddingBottom: offsetBottom,
              paddingHorizontal: paddingHorizontal || 0,
              paddingLeft: paddingLeft || 0,
            }}
          >
            <View
              style={[
                {
                  backgroundColor: C(bg),
                  overflow: 'hidden',
                  borderTopLeftRadius: scale(borderRadius),
                  borderTopRightRadius: scale(borderRadius),
                },
                isPhone && !asAlert
                  ? {
                      width: width,
                      flex: 1,
                    }
                  : {
                      maxHeight,
                      width: isPhone ? '100%' : typeof modalWidth === 'function' ? modalWidth({ fullWidth }) : modalWidth,
                      borderBottomLeftRadius: scale(borderRadius),
                      borderBottomRightRadius: scale(borderRadius),
                      ...(useFullHeight && { height: maxHeight }),
                    },
                isPhone &&
                  !asAlert &&
                  centered && {
                    justifyContent: 'center',
                    alignContent: 'center',
                    alignItems: 'center',
                  },
                !isPhone &&
                  noPhoneHeight && {
                    height: noPhoneHeight,
                    maxHeight: noPhoneHeight,
                  },
              ]}
            >
              {Component ? <Component {...newProps} /> : null}
            </View>
          </View>
        </Animated.View>
      </KeyboardAvoidingView>
    </View>
  );

  return (
    <ModalWrapper
      isVisible={isVisible}
      C={C}
      onBackdropPress={shouldCloseOnPressOutside ? handleClose : () => {}}
      backdropColor={backdropColor}
      backdropOpacity={1}
      animationIn={animationIn}
      animationOut={animationOut}
      animationInTiming={MODAL_ANIMATION_TIMING}
      animationOutTiming={MODAL_ANIMATION_TIMING}
    >
      {Content}
    </ModalWrapper>
  );
};

const RenderComponent = connect(null, {})(ColorsWrapper(FrameComponent));

class Popup extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      storage: [],
    };
  }

  show = ({
    Component,
    id,
    width = 480,
    paddingHorizontal = 0,
    paddingLeft = 0,
    customDuration = 300,
    backdropColor,
    avoidingViewEnable,
    bg,
    centered,
    asAlert,
    isPhoneSmall,
    noPhoneHeight,
    offsetBottom,
    offsetTop,
    useFullHeight,
    bottomSheet,
    borderRadius,
    centeredHorizontally = true,
    centeredVertically = true,
    shouldCloseOnPressOutside = false,
    callBackOnClose,
    animationIn,
    animationOut,
  }) => {
    const exist = id && this.state.storage?.find((i) => i.modalId == id);
    if (this.state.delay || exist) {
      return;
    }
    const arr = [
      ...this.state.storage,
      {
        Component,
        backdropColor,
        bg,
        centered,
        asAlert,
        isPhoneSmall,
        noPhoneHeight,
        width,
        paddingHorizontal,
        paddingLeft,
        customDuration,
        id: this.state.storage.length + 100,
        avoidingViewEnable,
        modalId: id,
        offsetBottom,
        offsetTop,
        useFullHeight,
        bottomSheet,
        borderRadius,
        centeredHorizontally,
        centeredVertically,
        shouldCloseOnPressOutside,
        callBackOnClose,
        isVisible: true,
        animationIn,
        animationOut,
      },
    ];
    this.setState({ storage: arr, delay: true });
    setTimeout(() => {
      this.setState({ delay: false });
    });
  };

  hide = (id) => {
    // First set isVisible to false for animation
    const storage = this.state.storage.map((item) => {
      if (item.id === id || item.modalId === id) {
        return { ...item, isVisible: false };
      }
      return item;
    });

    this.setState({ storage });

    // Remove from storage after animation completes
    setTimeout(() => {
      this.setState({
        storage: this.state.storage.filter((i) => i.id !== id && i.modalId !== id),
      });
    }, 300);
  };

  render() {
    if (!this.state.storage.length) {
      return null;
    }

    return this.state.storage.map((data) => (
      <RenderComponent key={data.id + '-popup'} {...{ ...this.props, ...data, hide: () => this.hide(data.id) }} />
    ));
  }
}

export default Popup;

export const PopupWrapper = ColorsWrapper((props) => {
  if (isWeb) {
    return props.children;
  }

  const { C, centeredHorizontally = true, centeredVertically = true } = props;

  const offsetBottom = scale(isPhone ? 0 : 50);
  const offsetTop = scale(isPhone ? 0 : 50);
  const maxHeight = height - (offsetBottom + offsetTop);

  return (
    <View
      style={{
        position: 'absolute',
        width: '100%',
        height: '100%',
        backgroundColor: C('Overlay/Light/Dim strong'),
        ...(centeredHorizontally ? { alignItems: 'center' } : {}),
        top: 0,
      }}
    >
      <View
        style={{
          height: '100%',
          ...(centeredVertically ? { justifyContent: 'center' } : {}),
          paddingTop: isPhone ? (isAndroid ? scale(20) : 0) : offsetTop,
          paddingBottom: offsetBottom,
        }}
      >
        <View
          style={[
            {
              backgroundColor: C('Background/Light/Panel'),
              overflow: 'hidden',
              borderTopLeftRadius: scale(SCREEN_BORDER_RADIUS),
              borderTopRightRadius: scale(SCREEN_BORDER_RADIUS),
            },
            isPhone
              ? {
                  width: width,
                  flex: 1,
                }
              : {
                  maxHeight,
                  width: scale(480),
                  borderBottomLeftRadius: scale(SCREEN_BORDER_RADIUS),
                  borderBottomRightRadius: scale(SCREEN_BORDER_RADIUS),
                },
          ]}
        >
          {React.cloneElement(props.children, {
            hide: navigateBack,
            maxHeight,
          })}
        </View>
      </View>
    </View>
  );
});
